import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { App as CapacitorApp, BackButtonListenerEvent } from "@capacitor/app";
import { PluginListenerHandle } from "@capacitor/core";
import { Toaster } from "@/components/ui/sonner";
import { captureWebHandoffLink } from "@/services/webOnboardingSession";
// Lazy for the same reason every page below is: these two pull in the whole
// quests subtree — the ladder modal, its cards, `useQuests` and its stores —
// and the ad landing page has no ladder at all. Both self-hide, so a frame
// without them is the state they render anyway.
const DentalPromptsHost = lazy(() =>
  import("@/features/dental/components/DentalPromptsHost").then((m) => ({
    default: m.DentalPromptsHost,
  }))
);
const SmileProgressPage = lazy(() =>
  import("@/features/dental/pages/SmileProgressPage").then((m) => ({
    default: m.SmileProgressPage,
  }))
);
const DentalPreVisitStoriesPage = lazy(() =>
  import("@/features/dental/pages/DentalPreVisitStoriesPage").then((m) => ({
    default: m.DentalPreVisitStoriesPage,
  }))
);
const DentalMindManualsPage = lazy(() =>
  import("@/features/dental/pages/DentalMindManualsPage").then((m) => ({
    default: m.DentalMindManualsPage,
  }))
);
const DentalMindManualPage = lazy(() =>
  import("@/features/dental/pages/DentalMindManualPage").then((m) => ({
    default: m.DentalMindManualPage,
  }))
);
const AchievementBanner = lazy(() =>
  import("@/components/quests/AchievementBanner").then((m) => ({
    default: m.AchievementBanner,
  }))
);
const RewardRevealHost = lazy(() =>
  import("@/components/quests/RewardRevealHost").then((m) => ({
    default: m.RewardRevealHost,
  }))
);
const LadderFinaleHost = lazy(() =>
  import("@/components/quests/LadderFinaleHost").then((m) => ({
    default: m.LadderFinaleHost,
  }))
);
import { useAuth } from "@um/ui";
import {
  useFeedbackStrategyObservability,
  useOnAppOpenFeedbackSchedule,
} from "@/services/feedback";
import { useExperimentAssignmentStamping } from "@/services/experiments";
import { useDentalPilotUserProperty } from "@/features/dental/hooks/useDentalPilotUserProperty";
import { useForegroundFirestoreReconnect } from "@/services/firestoreLifecycle";
import { logException } from "@um/ui";
import AppInitializer from "./components/AppInitializer";
import { ReportPageDrawer } from "./components/ReportPageDrawer";
import { SupportDrawer } from "./components/SupportDrawer";
import { SparkStoryDialog } from "./components/SparkStoryDialog";
import { SignInDialog } from "./components/auth/SignInDialog";
import { WishRevealHost } from "./components/items/WishRevealHost";
import { CancellationSurveyDrawer } from "./components/CancellationSurveyDrawer";
import { NotificationsAppIntentDialog } from "./components/ui/NotificationsAppIntentDialog";
import { DevSafeZonePreviewControlsGate } from "./components/DevSafeZonePreviewControls";

// Page imports.
//
// Everything except ExplorePage is lazy. /explore is the paid-ad landing page,
// so its cold first paint is the metric that matters most; a static import of
// any other page would drag that page (and its dependency tail) into the
// entry chunk that the ad visitor has to download before seeing anything.
// ExplorePage stays eager precisely because it IS the landing page — a lazy
// chunk there would just add a round trip.
import ExplorePage from "@/pages/ExplorePage";

const AuthPage = lazy(() => import("@/pages/AuthPage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const MainLayout = lazy(() => import("@/layouts/MainLayout"));
const CreateProfilePage = lazy(() => import("@/pages/CreateProfilePage"));
const ProfileDetailPage = lazy(() => import("@/pages/ProfileDetailPage"));
const ProfileChatPage = lazy(() => import("@/pages/ProfileChatPage"));
const MindManualsPage = lazy(() => import("@/pages/MindManualsPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ProfilesPage = lazy(() => import("@/pages/ProfilesPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const CharacterDetailPage = lazy(() => import("./pages/CharacterDetailPage"));
const CharacterCardPage = lazy(() => import("./pages/CharacterCardPage"));
const RetiredCharactersPage = lazy(
  () => import("./pages/RetiredCharactersPage")
);
const CharactersPage = lazy(() => import("./pages/CharactersPage"));
const ViewAnimalPage = lazy(() => import("./pages/ViewAnimalPage"));
const WaitForTaskPage = lazy(() => import("./pages/WaitForTaskPage"));
const AdoptionCenterPage = lazy(() => import("./pages/AdoptionCenterPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const MyStuffPage = lazy(() => import("./pages/MyStuffPage"));
const PrizeMachinePage = lazy(() => import("./pages/PrizeMachinePage"));
const MyStuffStoriesPage = lazy(() => import("./pages/MyStuffStoriesPage"));
const MyStuffStoryDetailPage = lazy(
  () => import("./pages/MyStuffStoryDetailPage")
);
const ActivityMemoriesPage = lazy(() => import("./pages/ActivityMemoriesPage"));
const MyStuffMemoriesListPage = lazy(
  () => import("./pages/MyStuffMemoriesListPage")
);
const UsersAccessPage = lazy(() => import("./pages/UsersAccessPage"));
const JoinPage = lazy(() => import("./pages/JoinPage"));
const DynamicContentPage = lazy(() => import("./pages/DynamicContentPage"));
const AllActionsPage = lazy(() => import("./pages/AllActionsPage"));
const HybridFlowPage = lazy(() => import("@/pages/mvp/flows/HybridFlowPage"));
const DigestPage = lazy(() => import("@/pages/DigestPage"));
const RoomResetPage = lazy(() => import("@/pages/RoomReset/RoomResetPage"));
const RoomResetRecapPage = lazy(
  () => import("@/pages/RoomReset/RoomResetRecapPage")
);
const SpaceFrequencyPage = lazy(() => import("@/pages/SpaceFrequencyPage"));
const ShowScreenPage = lazy(() =>
  import("@/pages/show/ShowScreens").then((module) => ({
    default: module.ShowScreenPage,
  }))
);
const ShowJoinPage = lazy(() =>
  import("@/pages/show/ShowScreens").then((module) => ({
    default: module.ShowJoinPage,
  }))
);
const ShowObserverPage = lazy(() => import("@/pages/show/ShowObserverPage"));
const PublicShowPreviewPage = lazy(
  () => import("@/pages/show/PublicShowPreviewPage")
);
const ShowViewPage = lazy(() => import("@/pages/show/ShowViewPage"));
const StoryEditorPage = lazy(() => import("@/pages/show/StoryEditorPage"));
const StoryReviewPage = lazy(() => import("@/pages/show/StoryReviewPage"));
const ScaleLabPage = lazy(() => import("@/pages/show/ScaleLabPage"));
const CreateStoryPage = lazy(() => import("@/pages/create/CreateStoryPage"));
const CustomizeStoryPage = lazy(() => import("@/pages/create/CustomizeStoryPage"));
const WorldBuilderPage = lazy(
  () => import("@/pages/show/world/WorldBuilderPage")
);
const AccountWorldPage = lazy(() => import("@/pages/worlds/AccountWorldPage"));
const WorldTravelPage = lazy(() => import("@/pages/worlds/WorldTravelPage"));
const WorldGiftPage = lazy(() => import("@/pages/worlds/WorldGiftPage"));
const StudioWaitingRoomPage = lazy(
  () => import("@/pages/studio/StudioWaitingRoomPage")
);
const PerformancePage = lazy(
  () => import("@/pages/dev/stage/StageScriptTestPage")
);
const RedeemPage = lazy(() => import("./pages/RedeemPage"));
const DevIndexPage = lazy(() => import("@/pages/dev/DevIndexPage"));
import { DEV_PAGES } from "@/pages/dev/devPages";
import {
  usePayments,
  PaywallHost,
  dismissPaywallUi,
  handleWebPurchaseRedemptionLink,
} from "@/services/paywall";
import { AppleEmailGate } from "@um/ui/components";
import { prepareShowNavigation } from "./pages/show/showViewNavigation";

// Component that wraps the app with navigation handling
const AppWrapper = () => {
  usePayments();
  const location = useLocation();
  const { user, accountId, account, accountSettings, role, initializing } =
    useAuth();

  // Emit FeedbackStrategy snapshot (raw RC value + source) + set GA user
  // properties as soon as we know the user, and again after Remote Config
  // activates. Keyed on the Firebase user (NOT account) because variant
  // assignment is per Firebase installation — two users sharing one account
  // can be on different variants and must each emit their own snapshot.
  // Lets us answer "is this user on a variant, on the default, or missing RC
  // entirely?" from GA without touching code.
  useFeedbackStrategyObservability(user?.uid, accountId);

  // Force Firestore listeners to reconnect after background -> foreground;
  // they can stall silently when the OS freezes the WebView (UM-61).
  useForegroundFirestoreReconnect();

  // Stamp the routine-menu experiment assignment (GA user property +
  // um_experiment_assigned event, Firestore Settings/account, RevenueCat
  // subscriber attribute) once user + account are known. Keyed on the user
  // for the same per-installation reason as the feedback observability hook.
  useExperimentAssignmentStamping(user?.uid, accountId);

  // Mirror the dental pilot flag to a GA user property, same as the cohort
  // markers above. Read from the loaded settings rather than stamped at
  // enrolment so it holds on a participant's second device.
  useDentalPilotUserProperty();

  useOnAppOpenFeedbackSchedule({
    accountId,
    account,
    accountSettings,
    initializing,
    locationPathname: location.pathname,
    role,
  });

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <DevSafeZonePreviewControlsGate />
      <Toaster duration={8000} className="mb-safe" />
      {/* Inside RouterProvider so dialog content can use router hooks / Link. */}
      <PaywallHost />
      <SignInDialog />
      <AppNavigationHandler />
      <AppInitializer />
      {/* Mounted once for the whole app so "report content" works on every
          route (Google Play AI-Generated Content policy: users must be able
          to report offensive content without exiting the app). */}
      <ReportPageDrawer />
      {/* Support & feedback form opened from the profile menu. */}
      <SupportDrawer />
      {/* Spark an on-demand story. Root-mounted so the support menu at
          /routines — which renders outside MainLayout — can open it too. */}
      <SparkStoryDialog />
      {/* Wish reveals appear only on Home; pending outfits finish on any route. */}
      <WishRevealHost />
      {/* Exit survey shown once when a subscription is newly set to not renew. */}
      <CancellationSurveyDrawer />
      <NotificationsAppIntentDialog hidden={false} />
      {/* Its own boundary: the one in `AppInitializer` wraps the route outlet
          only, and these are siblings of it. Blank fallback, as there — both
          components render nothing until they have something to say. */}
      <Suspense fallback={null}>
        {/* Announces a finished step wherever the user happens to be — the thing
            that completes one is usually nowhere near the ladder. Must be inside
            RouterProvider: it opens the quests modal, which navigates. */}
        {/* Dental pilot check-in and feedback prompts. Self-gating: renders
            nothing for an account with no dental support. Lazy, like its
            neighbours — a static import pulled the whole dental feature and
            canvas-confetti into the entry chunk for every user. */}
        <DentalPromptsHost />
        <AchievementBanner />
        {/* The claim confirmation. App-wide because the card that claims
            unmounts itself the instant the claim lands — see
            `quests/pendingReveal.ts`. */}
        <RewardRevealHost />
        {/* The end of the ladder: celebration and survey. App-wide because the
            last reward can be ordered from any route, and because the survey
            must not end up behind the modals that led to it. */}
        <LadderFinaleHost />
      </Suspense>
      <AppleEmailGate />
    </div>
  );
};

// Shared public routes
const sharedPublicRoutes = [
  { path: "/studio/waiting-room", element: <StudioWaitingRoomPage /> },
  { path: "/show/observe/:sessionId", element: <ShowObserverPage /> },
  {
    path: "/show",
    element: <PerformancePage release />,
  },
  // Accelerated ad landing. Rendered immediately by AppInitializer (no init
  // loading screen), and lives inside AppWrapper so it shares the single
  // useAuthInit instance — enabling client-side navigation into onboarding.
  {
    path: "/explore",
    element: <ExplorePage />,
  },
  {
    path: "/privacy",
    element: <PrivacyPolicyPage />,
  },
  {
    path: "/terms",
    element: <TermsOfServicePage />,
  },
  {
    path: "/view/:animalId/:personaId?",
    element: <ViewAnimalPage />,
  },
  {
    path: "/waitfortask/:taskId",
    element: <WaitForTaskPage />,
  },
  {
    path: "/join/:inviteCode",
    element: <JoinPage />,
  },
  // Wholesaler purchase code redemption (mirrors the RC web-purchase redemption UX).
  {
    path: "/redeem/:code",
    element: <RedeemPage />,
  },
  // QR-code landing page: public so a visitor can experience the tuner without
  // first having to sign in. Character data only loads if they happen to be
  // authenticated; otherwise the page shows a sign-in CTA in place of the
  // channel guide.
  {
    path: "/space",
    element: <SpaceFrequencyPage />,
  },
  {
    path: "/space/:characterId",
    element: <SpaceFrequencyPage />,
  },
  // Component harnesses. Dev-only, and public so they load without an account.
  //
  // Generated from the register in pages/dev/devPages.ts, which the index at
  // /dev reads as well -- so a harness cannot be routable but unlisted, or
  // listed but unroutable.
  ...(import.meta.env.DEV
    ? [
        { path: "/dev", element: <DevIndexPage /> },
        ...DEV_PAGES.map((page) => ({
          path: page.path,
          element: <page.component />,
        })),
      ]
    : []),
];

// Route configuration
const createRoutes = () => [
  {
    path: "/auth/*",
    element: <AuthPage />,
  },
  {
    path: "/onboarding",
    element: <HybridFlowPage defaultFlowType="onboarding" />,
  },
  // Public routes: no auth required, and AppInitializer must never hijack them
  // with the forced-onboarding redirect. The publicRoute handle is read via
  // useMatches so this exemption applies automatically to every route here.
  {
    handle: { publicRoute: true },
    children: sharedPublicRoutes,
  },
  {
    handle: { requiresAuth: true },
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            path: "/",
            element: <HomePage />,
          },
          {
            path: "/manuals",
            element: <MindManualsPage />,
          },
          {
            path: "/notifications",
            element: <NotificationsPage />,
          },
          {
            path: "/characters",
            element: <CharactersPage />,
          },
          {
            path: "/characters/retired",
            element: <RetiredCharactersPage />,
          },
          {
            path: "/characters/:characterId/card",
            element: <CharacterCardPage />,
          },
          {
            path: "/adoption-center",
            element: <AdoptionCenterPage />,
          },
          {
            path: "/profiles",
            element: <ProfilesPage />,
          },
          {
            path: "/my-stuff",
            element: <MyStuffPage />,
          },
          {
            path: "/prize-machine",
            element: <PrizeMachinePage />,
          },
          {
            path: "/my-stuff/smile-progress",
            element: <SmileProgressPage />,
          },
          {
            path: "/my-stuff/dental-stories",
            element: <DentalPreVisitStoriesPage />,
          },
          {
            path: "/my-stuff/dental-manuals",
            element: <DentalMindManualsPage />,
          },
          {
            path: "/my-stuff/dental-manual/:manualId",
            element: <DentalMindManualPage />,
          },
          {
            path: "/my-stuff/memories",
            element: <MyStuffMemoriesListPage />,
          },
          {
            path: "/my-stuff/memories/:activityId",
            element: <ActivityMemoriesPage />,
          },
          {
            path: "/my-stuff/stories",
            element: <MyStuffStoriesPage />,
          },
          {
            path: "/my-stuff/stories/:storyId",
            element: <MyStuffStoryDetailPage />,
          },
        ],
      },
      // Routes without bottom nav
      {
        path: "/profiles/new",
        element: <CreateProfilePage />,
      },
      {
        path: "/profiles/:profileId",
        element: <ProfileDetailPage />,
      },
      {
        path: "/profiles/:profileId/chat",
        element: <ProfileChatPage />,
      },
      {
        path: "/profiles/:profileId/mind-manuals",
        element: <MindManualsPage />,
      },
      {
        path: "/chat/:chatId?",
        element: <ChatPage />,
      },
      {
        path: "/users-access",
        element: <UsersAccessPage />,
      },
      {
        path: "/activity/:paramContentId",
        element: <DynamicContentPage />,
      },
      {
        path: "/actions",
        element: <AllActionsPage />,
      },
      {
        path: "/characters/new",
        element: <CharacterDetailPage isNewCharacterFlowParam={true} />,
      },
      {
        path: "/characters/:characterId",
        element: <CharacterDetailPage isNewCharacterFlowParam={false} />,
      },
      {
        path: "/flows/character-creation",
        element: <HybridFlowPage defaultFlowType="character-creation" />,
      },
      {
        path: "/flows/champion-setup",
        element: <HybridFlowPage defaultFlowType="champion-setup" />,
      },
      {
        path: "/routines",
        element: <HybridFlowPage defaultFlowType="routine-selection" />,
      },
      {
        path: "/flows/room-reset",
        element: <HybridFlowPage defaultFlowType="room-reset" />,
      },
      {
        path: "/room-reset/:jobId",
        element: <RoomResetPage />,
      },
      {
        path: "/room-reset/:jobId/recap",
        element: <RoomResetRecapPage />,
      },
      {
        path: "/digest/:digestId",
        element: <DigestPage />,
      },
    ],
  },
];

const routes = createRoutes();

// Guests need only the isolated show client. Mounting AppInitializer here
// would also subscribe this screen to the director's account and settings,
// and bring purchase, achievement, and notification overlays onto the stage.
const ShowGuestShell = () => (
  <>
    <SignInDialog />
    <Toaster theme="dark" position="top-center" richColors closeButton />
    <Suspense
      fallback={
        <div
          className="flex min-h-dvh items-center justify-center bg-slate-950 text-amber-100"
          role="status"
        >
          Setting the stage…
        </div>
      }
    >
      <Outlet />
    </Suspense>
  </>
);

const router = createBrowserRouter([
  {
    element: <ShowGuestShell />,
    children: [
      { path: "/show/preview", element: <PublicShowPreviewPage /> },
      { path: "/show/preview/:storyId", element: <PublicShowPreviewPage /> },
      {
        path: "/show/preview/:accountId/:storyId",
        element: <PublicShowPreviewPage />,
      },
      { path: "/show/review/:storyId", element: <StoryReviewPage /> },
      { path: "/show/edit/:accountId/:storyId", element: <StoryEditorPage /> },
      { path: "/show/scales/:accountId/:storyId", element: <ScaleLabPage /> },
      { path: "/world-gift/:releaseId/:mode?", element: <WorldGiftPage /> },
      { path: "/worlds", element: <WorldTravelPage /> },
      { path: "/worlds/:ownerAccountId", element: <WorldTravelPage /> },
      {
        path: "/worlds/:ownerAccountId/:worldId",
        element: <AccountWorldPage />,
      },
      {
        path: "/show/world/:accountId/:storyId",
        element: <WorldBuilderPage />,
      },
      {
        path: "/show/world/:accountId/:storyId/new-character",
        element: <WorldBuilderPage />,
      },
      { path: "/create", element: <CreateStoryPage /> },
      { path: "/show/customize/:accountId/:storyId", element: <CustomizeStoryPage /> },
      { path: "/create/draft/:draftId", element: <CreateStoryPage /> },
      { path: "/create/:accountId/:storyId", element: <CreateStoryPage /> },
      { path: "/gram", element: <Navigate to="/create" replace /> },
      { path: "/gram/:accountId/:storyId", element: <CreateStoryPage /> },
      { path: "/show/view", element: <ShowViewPage /> },
      { path: "/show/screen", element: <ShowScreenPage /> },
      { path: "/show/join/:code", element: <ShowJoinPage /> },
      { path: "/show/pair/:code", element: <ShowJoinPage /> },
    ],
  },
  {
    path: "/",
    element: <AppWrapper />,
    children: [
      ...routes,
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);

// Router wrapper
const AppRouterWrapper = () => {
  // Both cold and warm links must work while a public show screen is open.
  // Keeping this above the route shells avoids requiring account initialization.
  useEffect(() => {
    const open = async (value: string) => {
      if (captureWebHandoffLink(value)) return;
      let target: ReturnType<typeof prepareShowNavigation>;
      try {
        target = prepareShowNavigation(new URL(value));
      } catch {
        return;
      }
      const { path, state } = target;
      if (!path.startsWith("/") || path.startsWith("//")) return;
      // Sanitize show handoffs before any helper or router can observe a URL.
      if (state?.showHandoffNonce) {
        await router.navigate(path, { state });
        return;
      }
      if (await handleWebPurchaseRedemptionLink(value)) return;
      if (path.startsWith("/redeem")) dismissPaywallUi();
      await router.navigate(path);
    };
    const listener = CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      if (url) void open(url).catch(logException);
    });
    void CapacitorApp.getLaunchUrl()
      .then((result) => {
        if (result?.url) return open(result.url);
      })
      .catch(logException);
    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, []);
  return <RouterProvider router={router} />;
};

// New component to handle navigation-dependent logic
const AppNavigationHandler = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();

  // const {
  //   setUserId,
  //   setDeepLinkHandler,
  //   setAuthenticationStatus,
  //   setAuthInitializingStatus,
  // } = useFirebaseMessagingService();

  useEffect(() => {
    let listenerPromise: Promise<PluginListenerHandle | undefined> =
      (async () => {
        try {
          const listenerHandle = await CapacitorApp.addListener(
            "backButton",
            (event: BackButtonListenerEvent) => {
              const currentPath = window.location.pathname;
              const homePath = "/";
              // Define your main tab paths here
              const mainTabPaths = [
                homePath,
                "/home",
                "/manuals",
                "/notifications",
                "/characters",
                "/adoption-center",
                "/profiles",
              ];

              // Don't navigate away from onboarding — accidental back gestures
              // (Android edge swipe) would abandon the flow mid-progress.
              if (currentPath === "/onboarding") {
                return;
              }

              if (mainTabPaths.includes(currentPath)) {
                // Current path is one of the main tabs
                if (currentPath === homePath || currentPath === "/home") {
                  // If on home tab, exit the app
                  CapacitorApp.exitApp();
                } else {
                  // If on any other main tab, navigate to home
                  navigate(homePath);
                }
              } else {
                // Current path is NOT a main tab (it's a sub-page or a page not in the main nav)
                if (event.canGoBack) {
                  // If there's browser history, navigate back
                  navigate(-1);
                } else {
                  // No browser history (e.g. app launched directly into a deep link not on a main tab)
                  // Navigate to home as a fallback, so next back swipe from home exits.
                  navigate(homePath);
                }
              }
            }
          );
          return listenerHandle;
        } catch (error) {
          logException(error);
          console.error("Failed to add back button listener", error);
          return undefined;
        }
      })();

    return () => {
      listenerPromise.then((listenerHandle) => {
        if (listenerHandle) {
          listenerHandle.remove();
        }
      });
    };
  }, [navigate]);

  return null;
};

function App() {
  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <AppRouterWrapper />
    </div>
  );
}

export default App;
